import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../../hooks/useTheme';
import { formatPrice } from '../../../hooks/useCurrency';
import {
  dentalCapacityMeta,
  type DentalCurrency,
  type DentalPlan,
} from '../../../mocks/dental';

interface DentalFeaturesModalProps {
  plan: DentalPlan;
  currency: DentalCurrency;
  onClose: () => void;
}

function ModalContent({ plan, currency, onClose }: DentalFeaturesModalProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const accent = plan.accent;
  const bg = isLight ? '#ffffff' : '#04101f';
  const ink = isLight ? '#0a1628' : '#ffffff';
  const sub = isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.55)';
  const border = isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.09)';

  return (
    <div
      className="dental-page fixed inset-0 z-[9999] overflow-y-auto"
      style={{ backgroundColor: 'rgba(0,0,0,0.70)', backdropFilter: 'blur(5px)' }}
    >
      <div
        className="flex min-h-full items-start sm:items-center justify-center p-4 sm:py-10"
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Todas las funciones del plan ${plan.name} de InnoFlex Odontología`}
          className="relative w-full rounded-2xl border flex flex-col"
          style={{
            maxWidth: '40rem',
            background: bg,
            borderColor: accent + '40',
            boxShadow: `0 0 60px ${accent}20, 0 24px 64px rgba(0,0,0,0.45)`,
          }}
        >
          <div
            className="h-1 w-full rounded-t-2xl flex-shrink-0"
            style={{ background: `linear-gradient(to right, ${accent}, ${accent}60, transparent)` }}
          />

          <div className="p-5 sm:p-7">
            {/* Encabezado */}
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <span
                  className="inline-block text-[11px] font-semibold tracking-widest uppercase font-body mb-1"
                  style={{ color: accent }}
                >
                  InnoFlex Odontología
                </span>
                <h3 className="font-heading text-xl sm:text-2xl font-bold" style={{ color: ink }}>
                  Plan {plan.name} — todas las funciones
                </h3>
                <p className="font-body text-xs sm:text-sm mt-1" style={{ color: sub }}>
                  {plan.from ? 'Desde ' : ''}
                  {formatPrice(plan.monthly[currency], currency)} por mes · Implementación{' '}
                  {plan.from ? 'desde ' : ''}
                  {formatPrice(plan.implementation[currency], currency)}
                </p>
              </div>
              <button
                ref={closeRef}
                onClick={onClose}
                aria-label="Cerrar"
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border transition-colors cursor-pointer hover:opacity-80"
                style={{
                  borderColor: border,
                  color: isLight ? 'rgba(10,22,40,0.65)' : 'rgba(255,255,255,0.50)',
                }}
              >
                <i className="ri-close-line text-base" aria-hidden="true" />
              </button>
            </div>

            {/* Capacidad */}
            <div
              className="rounded-xl border p-3 mb-5"
              style={{ borderColor: accent + '26', background: accent + (isLight ? '0d' : '12') }}
            >
              <p
                className="font-body text-[11px] font-bold uppercase tracking-wider mb-2"
                style={{ color: accent }}
              >
                Capacidad incluida
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {dentalCapacityMeta.map((meta) => (
                  <div key={meta.field} className="flex flex-col">
                    <span className="font-heading text-sm font-bold" style={{ color: ink }}>
                      {plan.capacity[meta.field]}
                    </span>
                    <span className="font-body text-[10px]" style={{ color: sub }}>
                      {meta.label}
                    </span>
                  </div>
                ))}
              </div>
              <p className="font-body text-[11px] mt-2" style={{ color: sub }}>
                Pacientes y citas ilimitados.
              </p>
            </div>

            {/* Funciones agrupadas */}
            <div className="flex flex-col gap-4">
              {plan.allFeatures.map((group) => (
                <div key={group.title}>
                  <h4
                    className="font-heading text-xs font-bold uppercase tracking-wider mb-2"
                    style={{ color: isLight ? 'rgba(10,22,40,0.70)' : 'rgba(255,255,255,0.65)' }}
                  >
                    {group.title}
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {group.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2 font-body text-[13px]"
                        style={{ color: isLight ? 'rgba(10,22,40,0.80)' : 'rgba(255,255,255,0.78)' }}
                      >
                        <i
                          className="ri-checkbox-circle-fill flex-shrink-0 mt-[2px]"
                          style={{ color: accent, fontSize: 13, lineHeight: 1 }}
                          aria-hidden="true"
                        />
                        <span style={{ lineHeight: 1.45 }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="font-body text-[11px] mt-5 leading-relaxed" style={{ color: sub }}>
              La implementación es un pago único, separado de la mensualidad. La facturación
              electrónica puede requerir certificación, configuración y/o servicios de terceros.
            </p>

            <button
              onClick={onClose}
              className="mt-5 w-full flex items-center justify-center gap-2 font-bold text-sm py-3 rounded-xl font-body transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: `linear-gradient(135deg, ${accent}, ${plan.accentDark})`,
                color: '#ffffff',
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DentalFeaturesModal(props: DentalFeaturesModalProps) {
  return createPortal(<ModalContent {...props} />, document.body);
}
